import React from 'react';
import { Link } from 'react-router-dom';
import { PHONE_DISPLAY, PHONE_TEL } from '../../constants/brand';
import { CORE_CITIES, METRO_CITIES, hasServiceCityPage } from '../../data/cities';
import { NAV_SERVICES, SERVICES } from '../../data/services';
import { locationPath, ROUTES, serviceCityPath, servicePath } from '../../enum/routes';
import CtaBand from '../../components/CtaBand';
import Seo from '../../components/Seo';

export default function ServiceAreasPage() {
  return (
    <>
      <Seo
        title="Browse by City | Texas Remodeling Markets | Epic Home Pros"
        description="Browse Epic Home Pros by city - Austin, Dallas, Houston, San Antonio, and surrounding Texas metros. Free remodel estimates."
        path={ROUTES.SERVICE_AREAS}
      />
      <section className="bg-forest-tint py-16 sm:py-24">
        <div className="container-page">
          <div className="mx-auto max-w-2xl text-center">
            <div className="label-eyebrow">Browse by City</div>
            <h1 className="h-display mt-3">Texas markets we serve</h1>
            <p className="mt-5 text-lg text-ink/75">
              Choose a city to see local remodeling pages and services. Primary focus: Austin, Dallas, Houston, and San
              Antonio.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="container-page">
          <h2 className="font-serif text-2xl text-ink sm:text-3xl">Primary metros</h2>
          <p className="mt-3 max-w-2xl text-ink/75">Dedicated city hubs with local context and links to each service.</p>
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
            {CORE_CITIES.map((city) => (
              <div
                key={city.slug}
                id={city.slug}
                className="card-hover scroll-mt-24 rounded-xl border border-black/10 bg-white p-6 shadow-sm hover:border-forest/40"
              >
                <Link to={locationPath(city.slug)}>
                  <h3 className="font-serif text-xl text-ink">
                    {city.name}, {city.state}
                  </h3>
                </Link>
                <p className="mt-1 text-xs uppercase tracking-wider text-ink/50">{city.regionLabel}</p>
                <ul className="mt-4 space-y-1.5">
                  {SERVICES.filter((service) => hasServiceCityPage(service.slug, city.slug)).map((service) => (
                    <li key={service.slug}>
                      <Link
                        className="inline-flex items-center gap-1 text-sm font-medium text-forest hover:text-forest-hover hover:underline"
                        to={serviceCityPath(service.slug, city.slug)}
                      >
                        {service.name} in {city.name}
                      </Link>
                    </li>
                  ))}
                  {NAV_SERVICES.filter((service) => !hasServiceCityPage(service.slug, city.slug))
                    .slice(0, 4)
                    .map((service) => (
                      <li key={service.slug}>
                        <Link
                          className="inline-flex items-center gap-1 text-sm font-medium text-forest hover:text-forest-hover hover:underline"
                          to={servicePath(service.slug)}
                        >
                          {service.name}
                        </Link>
                      </li>
                    ))}
                </ul>
                <Link
                  className="mt-5 inline-block text-sm font-semibold text-ink hover:text-forest"
                  to={locationPath(city.slug)}
                >
                  View {city.name} overview →
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-cream py-16 sm:py-20">
        <div className="container-page">
          <h2 className="font-serif text-2xl text-ink sm:text-3xl">Surrounding cities</h2>
          <p className="mt-3 max-w-2xl text-ink/75">
            Additional coverage near Dallas-Fort Worth and Greater Houston. Quality pages with local notes - not thin
            duplicates.
          </p>
          <ul className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {METRO_CITIES.map((city) => (
              <li key={city.slug}>
                <Link
                  className="card-hover block rounded-xl border border-black/10 bg-white p-5 shadow-sm hover:border-forest/40"
                  to={locationPath(city.slug)}
                >
                  <h3 className="font-serif text-xl text-ink">
                    {city.name}, {city.state}
                  </h3>
                  <p className="mt-1 text-xs uppercase tracking-wider text-ink/50">{city.regionLabel}</p>
                  <span className="mt-3 inline-block text-sm font-medium text-forest">City page →</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="container-page">
          <h2 className="font-serif text-2xl text-ink sm:text-3xl">Services statewide</h2>
          <ul className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {NAV_SERVICES.map((s) => (
              <li key={s.slug}>
                <Link
                  className="block rounded-xl border border-black/10 bg-white p-5 shadow-sm hover:border-forest/40"
                  to={servicePath(s.slug)}
                >
                  <span className="font-serif text-lg text-ink">{s.name}</span>
                  <span className="mt-1 block text-sm text-forest">Details &amp; FAQs →</span>
                </Link>
              </li>
            ))}
          </ul>
          <p className="mt-6 text-sm">
            <Link className="font-medium text-forest hover:underline" to={ROUTES.SERVICES}>
              View all services →
            </Link>
            {' · '}
            <Link className="font-medium text-forest hover:underline" to={servicePath('outdoor-living')}>
              Outdoor living overview hub →
            </Link>
          </p>
          <p className="mt-8 text-sm text-ink/70">
            Prefer to talk? Call{' '}
            <a href={`tel:${PHONE_TEL}`} className="font-semibold text-forest hover:underline">
              {PHONE_DISPLAY}
            </a>
            .
          </p>
        </div>
      </section>

      <CtaBand title="Don't see your exact city?" lead="Give us a call or request a quote and we'll confirm whether we can help." />
    </>
  );
}
